import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import ExportDataDropDown from '@/components/molecules/ExportDataDropDown';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import LICENSES from '@/config/licenses';
import { useCage, useCageName, useCageTimezone } from '@/contexts/cage';
import { useCageFilter } from '@/contexts/cage-filter-context';
import { usePenCohortFishGroup } from '@/contexts/pen-cohort-context';
import { useGetCageWelfareOverview } from '@/domain/cage-welfare-overview';
import { speciesTraits } from '@/domain/cages/speciesTraits';
import { fullSizeImageMapper, useGetWelfareImages } from '@/domain/images';
import type { DailyWelfareData } from '@/domain/welfare/types';
import { useGetFullWelfare } from '@/domain/welfare/useGetFullWelfare';
import { useImageState } from '@/hooks/image-state';
import { useQueryParams } from '@/hooks/useQueryParams';
import CageWelfareWoundsDashboardPageContent from '@/routes/Client/Cage/pages/CageWelfare/CageWelfareWounds/pages/CageWelfareWoundsDashboard/CageWelfareWoundsDashboardPageContent';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { dayStartISOInTimezone, toDateString } from '@/utils/date';
import { formatMachineNumber } from '@/utils/localeFormatters';
import type { ParsedQuery } from '@/utils/queryParams';
import { MERGED_WELFARE_IMAGES_TYPE } from '@/utils/welfareImageHelpers';

const downloadableData = (
    dailyHealthData: DailyWelfareData[],
    queryParams?: ParsedQuery<string>,
    hasBiomassWithQualityLicense?: boolean
) => {
    const DEFAULT_DAYPART_TAG_FOR_DOWNLOAD = 'day';
    const daypartTagForDownload = queryParams?.d ? queryParams.d : DEFAULT_DAYPART_TAG_FOR_DOWNLOAD;
    const dailyHealthDataForDownload =
        dailyHealthData?.map((el) => ({
            date: el?.day,
            daypartTag: daypartTagForDownload,
            count: el?.count,
            wound: formatMachineNumber(el?.wound, 2),
            scaleLoss: formatMachineNumber(el?.skinSpeckles, 2),
            smallWound: formatMachineNumber(el?.smallWound, 2),
            mediumWound: formatMachineNumber(el?.mediumWound, 2),
            bigWound: formatMachineNumber(el?.bigWound, 2),
            singleWound: formatMachineNumber(el?.oneWound, 2),
            moreThanOneWound: formatMachineNumber(el?.moreThanOneWound, 2),
            estimatedDowngrade: hasBiomassWithQualityLicense
                ? (formatMachineNumber(el?.notSuperior, 2) ?? '-')
                : '-',
        })) ?? [];
    // need to send columns names in this format
    const locationInfoColumns = [
        'location.name',
        'location.timezone',
        'location.sWeightPct',
        'cage.name',
    ];
    const dataColumnNames =
        dailyHealthDataForDownload.length > 0 ? Object.keys(dailyHealthDataForDownload[0]) : [];
    const formattedDataColumnNames = dataColumnNames.map((e) => `day.${e}`);
    const allColumnNames = [...locationInfoColumns, ...formattedDataColumnNames];
    return { dailyHealthDataForDownload, allColumnNames };
};

export default function CageWelfareWoundsDashboardContainer({
    clientId,
    locationId,
    cageId,
}: CagePageInterface) {
    const { t } = useTranslation();
    const { hasCageLicense, loading: cageLoading, stock } = useCage();
    const timezone = useCageTimezone();
    const cageName = useCageName();

    const hasBiomassWithQualityLicense = hasCageLicense(LICENSES.BIOMASS_WITH_QUALITY);
    // The estimate is withheld until the pen's species is known, so a trout pen never shows it
    // for the moment the context takes to resolve. The export below keeps its own column.
    const showDowngradeEstimate =
        hasBiomassWithQualityLicense &&
        !cageLoading &&
        speciesTraits(stock?.fishType?.shortName).woundInsightDowngradeEstimate;

    const { normalizedFrom, normalizedTo, daypartTag, setFilter, selectedFishGroupId } =
        useCageFilter();
    // The picker gets the URL's calendar days and the request gets the instant each day
    // starts at the site. The picker reads a day in the browser's zone, so the site's
    // midnight is the day before in any browser west of the site.
    const fromDay = normalizedFrom ? toDateString(normalizedFrom) : undefined;
    const toDay = normalizedTo ? toDateString(normalizedTo) : undefined;
    const from = fromDay ? dayStartISOInTimezone(fromDay, timezone) : undefined;
    const to = toDay ? dayStartISOInTimezone(toDay, timezone) : undefined;

    const welfareOverviewQuery = useGetCageWelfareOverview(
        cageId,
        { fishGroupId: selectedFishGroupId },
        {
            staleTime: 5 * 60 * 1000,
            gcTime: 5 * 60 * 1000,
        }
    );
    const {
        isLoading: cageWelfareOverviewIsLoading,
        data: cageWelfareOverviewData,
        error: cageWelfareOverviewError,
    } = welfareOverviewQuery;

    const cageWelfareOverview = cageWelfareOverviewData?.welfare;

    // Load data
    const fullWelfareQuery = useGetFullWelfare(
        cageId,
        {
            from,
            to,
            daypartTag: daypartTag || 'day',
            fishGroupId: selectedFishGroupId,
        },
        {
            staleTime: 5 * 60 * 1000,
        }
    );
    const { data: fishHealth, isLoading: fishHealthLoading } = fullWelfareQuery;
    const loading = cageLoading || fishHealthLoading || cageWelfareOverviewIsLoading;

    const imagesFishGroup = usePenCohortFishGroup();
    const imageDetections = useGetWelfareImages(
        cageId,
        {
            type: MERGED_WELFARE_IMAGES_TYPE,
            numberOfImages: '1',
        },
        { fishGroup: imagesFishGroup }
    );
    const imageState = useImageState({
        loading: imageDetections.isLoading && !imageDetections.error,
        data: imageDetections.data,
        mapper: useCallback(fullSizeImageMapper(cageId), []),
        queryParam: 'imageId',
        timezone: timezone,
    });

    const [queryParams] = useQueryParams();
    const { dailyHealthDataForDownload, allColumnNames } = downloadableData(
        fishHealth.welfareData?.days ?? [],
        queryParams,
        hasBiomassWithQualityLicense
    );

    if (loading) {
        return <Spinner size="lg" fullScreen />;
    }
    // The page's title goes with the content, so the error state stands in as its h1.
    if (cageWelfareOverviewError || !fishHealth) {
        return (
            <SomethingHappened
                headingLevel="h1"
                {...retryFailedQueries(welfareOverviewQuery, fullWelfareQuery)}
            />
        );
    }

    const handleSetCageFilter = (dateReturn: DateRange) => {
        setFilter({
            from: dateReturn.dateFrom,
            to: dateReturn.dateTo,
        });
    };

    return (
        <PageLayout
            title={cageName}
            subtitle={t('cageWelfare.common.woundInsight')}
            actions={
                <ExportDataDropDown>
                    <DownloadOptions data={dailyHealthDataForDownload} columns={allColumnNames} />
                </ExportDataDropDown>
            }>
            <CageWelfareWoundsDashboardPageContent
                clientId={clientId}
                locationId={locationId}
                cageId={cageId}
                fishHealthData={fishHealth.welfareData}
                bodyWoundsData={fishHealth.bodyWoundsData}
                images={imageState.loading ? [] : imageState.images}
                imagesLoading={imageState.loading}
                cageFilterHandler={handleSetCageFilter}
                cageWelfareOverview={cageWelfareOverview}
                inputDateObject={{
                    dateFrom: fromDay,
                    dateTo: toDay,
                }}
                showDowngradeEstimate={showDowngradeEstimate}
            />
        </PageLayout>
    );
}
